import { CorporateClubMemberExtractResponse } from '@smiles/voebiz-core-corporate-club/dist/Types/GetCorporateClubMemberExtractApi.types';
import { IGetListPurchaseMilesByBeneficiaryResponse } from '@smiles/voebiz-core-corporate-club/dist/Types/GetListPurchaseMilesByBeneficiaryApi.types';

import React, { FC, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getMemberExtract, getMemberHistory } from 'services';

export interface ContextHistory {
  memberExtract: CorporateClubMemberExtractResponse;
  memberHistory: IGetListPurchaseMilesByBeneficiaryResponse;
  loading: boolean;
  isEmptyExtract: boolean;
  isEmptyHistory: boolean;
  isLastPage: boolean;
  showMore: () => void;
  isExtractPage: boolean;
}

const extract = {
  memberExtract: {} as CorporateClubMemberExtractResponse,
  memberHistory: {} as IGetListPurchaseMilesByBeneficiaryResponse,

  loading: false,
  isEmptyExtract: false,
  isEmptyHistory: false,
  isLastPage: false,
  showMore: () => undefined,
  isExtractPage: false,
};

const emptyState = {
  memberExtractList: [],
  isLastPage: true,
  status: 'INACTIVE',
  currentPlan: '',
} as CorporateClubMemberExtractResponse;

const emptyStateHistory = {
  campaignName: '',
  id: '',
  amount: 0,
  status: 'INACTIVE',
  created: '',
} as IGetListPurchaseMilesByBeneficiaryResponse;

const MemberExtractContext = createContext<ContextHistory>(extract);

export const MemberExtractProvider: FC<{ children: JSX.Element }> = ({ children }) => {
  const [loading, setLoading] = useState(false);

  const [isExtractPage, setIsExtractPage] = useState(false);

  const [isLastPage, setIsLastPage] = useState(false);

  const [cpf, setCpf] = useState('');

  const [pageSize, setPageSize] = useState(10);
  const [memberExtract, setMemberExtract] = useState<CorporateClubMemberExtractResponse>(emptyState);
  const [memberHistory, setMemberHistory] = useState<IGetListPurchaseMilesByBeneficiaryResponse>(emptyStateHistory);
  const startRowNum = 0;

  const showMore = useCallback(() => {
    setPageSize((pageSize ?? 5) + 5);
  }, [pageSize]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data && event.data.type === 'URL_CHANGE') {
        const urlCpf = event.data.cpf;
        const isExtract = event.data.isExtractPage;
        setCpf(urlCpf);
        setIsExtractPage(isExtract);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);
  const getMemberExtractDetail = useCallback(() => {
    setLoading(true);
    getMemberExtract({ startRowNum, pageSize, cpf })
      .then(({ data }) => {
        if (data) {
          setIsLastPage(data.isLastPage);
          setMemberExtract(data);
        } else {
          setMemberExtract(emptyState);
        }
      })
      .finally(() => setLoading(false));
  }, [pageSize, cpf]);

  const getMemberHistoryDetail = useCallback(() => {
    setLoading(true);
    getMemberHistory({ startRowNum: startRowNum.toString(), pageSize: pageSize.toString(), cpf })
      .then(({ data }) => {
        if (data) {
          setIsLastPage(data.isLastPage);
          setMemberHistory(data);
        } else setMemberHistory(emptyStateHistory);
      })
      .finally(() => setLoading(false));
  }, [pageSize, cpf]);

  useEffect(() => {
    getMemberExtractDetail();
    getMemberHistoryDetail();
  }, [getMemberExtractDetail, getMemberHistoryDetail, isExtractPage, cpf]);

  const isEmptyExtract = !memberExtract?.memberExtractList || memberExtract?.memberExtractList.length === 0;

  const isEmptyHistory =
    !memberHistory?.transactionsPurchaseMiles || memberHistory?.transactionsPurchaseMiles.length === 0;

  const refresh = () => {
    getMemberExtractDetail();
    getMemberHistoryDetail();
  };

  useEffect(() => {
    const handleRefresh = (event) => {
      if (event.data && event.data.type === 'REFRESH') {
        refresh();
      }
    };

    window.addEventListener('message', handleRefresh);

    return () => {
      window.removeEventListener('message', handleRefresh);
    };
  }, [refresh]);

  const value = useMemo(
    () => ({
      memberExtract,
      memberHistory,
      loading,
      isLastPage,
      isEmptyExtract,
      isEmptyHistory,
      showMore,
      refresh,
      isExtractPage,
      cpf,
    }),
    [
      memberExtract,
      memberHistory,
      loading,
      isLastPage,
      isEmptyExtract,
      isEmptyHistory,
      showMore,
      refresh,
      isExtractPage,
      cpf,
    ],
  );

  return <MemberExtractContext.Provider value={value}>{children}</MemberExtractContext.Provider>;
};

export const useMemberExtract = () => {
  const MemberExtract = useContext(MemberExtractContext);

  if (!MemberExtract) {
    throw new Error('Cannot use `useMemberExtract` outside of MemberExtractProvider');
  }
  return MemberExtract;
};
